import { useState } from 'react'
import ReactDOM from 'react-dom'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box
} from '@mui/material'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Error from '@mui/icons-material/Error'
import Warning from '@mui/icons-material/Warning'
import Info from '@mui/icons-material/Info'

type DialogType = 'success' | 'error' | 'warning' | 'info'

interface InfoDialogAsyncState {
  open: boolean
  type: DialogType
  title: string
  message: React.ReactNode
  confirmText?: string
  btnSlot?: React.ReactNode
  resolve?: () => void
}

const iconMap = {
  success: <CheckCircle style={{ color: '#4CAF50', fontSize: 60 }} />,
  error: <Error style={{ color: '#F44336', fontSize: 60 }} />,
  warning: <Warning style={{ color: '#FF9800', fontSize: 60 }} />,
  info: <Info style={{ color: '#002678', fontSize: 60 }} />
}

const InfoDialogAsyncManager = ({
  state,
  closeDialog
}: {
  state: InfoDialogAsyncState
  closeDialog: () => void
}) => {
  const handleClose = () => {
    closeDialog()
    // Resolve the promise when dialog is closed
    if (state.resolve) {
      state.resolve()
    }
  }

  return ReactDOM.createPortal(
    <Dialog
      open={state.open}
      onClose={handleClose}
      maxWidth='xs'
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          overflowY: 'visible'
        }
      }}>
      <DialogTitle>
        <Typography
          sx={{
            fontWeight: '600',
            fontSize: '20px',
            textAlign: 'center',
            mt: 2,
            lineHeight: '1.6'
          }}>
          {state.title}
        </Typography>
      </DialogTitle>
      <Box
        sx={{
          position: 'absolute',
          left: '50%',
          height: '60px',
          width: '60px',
          transform: 'translate(-50%, -50%)',
          borderRadius: '50%',
          backgroundColor: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
        {iconMap[state.type]}
      </Box>
      <DialogContent sx={{ textAlign: 'center', pt: 0 }}>
        {typeof state.message === 'string' ? (
          <Typography variant='body2'>{state.message}</Typography>
        ) : (
          state.message
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
        {state.btnSlot ? (
          <Box
            onClick={handleClose}
            sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            {state.btnSlot}
          </Box>
        ) : (
          <Button
            onClick={handleClose}
            variant='contained'
            sx={{ width: '200px', height: '36px', alignSelf: 'center', mb: 2 }}>
            {state.confirmText || 'OK'}
          </Button>
        )}
      </DialogActions>
    </Dialog>,
    document.body
  )
}

const InfoDialogAsync = {
  _setState: (() => {}) as React.Dispatch<
    React.SetStateAction<InfoDialogAsyncState>
  >,

  open: ({
    type,
    title,
    message,
    confirmText,
    btnSlot
  }: {
    type: DialogType
    title: string
    message: React.ReactNode
    confirmText?: string
    btnSlot?: React.ReactNode
  }): Promise<void> => {
    return new Promise<void>(resolve => {
      InfoDialogAsync._setState({
        open: true,
        type,
        title,
        message,
        confirmText,
        btnSlot,
        resolve
      })
    })
  },

  close: () => {
    InfoDialogAsync._setState(prev => {
      // Resolve the promise if it exists
      if (prev.resolve) {
        prev.resolve()
      }
      return { ...prev, open: false, resolve: undefined }
    })
  },

  Provider: () => {
    const [state, setState] = useState<InfoDialogAsyncState>({
      open: false,
      type: 'success',
      title: '',
      message: '',
      confirmText: undefined,
      btnSlot: null,
      resolve: undefined
    })

    InfoDialogAsync._setState = setState

    return (
      <InfoDialogAsyncManager
        state={state}
        closeDialog={() => InfoDialogAsync.close()}
      />
    )
  }
}

export default InfoDialogAsync
